import quadrantsData from '../shared/quadrants.json';
import { assembleBoard } from '../shared/board.mjs';
import { buildWallMap } from '../shared/walls.mjs';
import { COLOR_NAMES } from '../logging';

// Robot hexes by name, derived from the map logging already keeps rather than
// typed out again. CLAUDE.md counts the copies of this palette for a reason —
// a fourth one that drifts would break these scenes with nothing to point at.
const HEX = Object.fromEntries(
  Object.entries(COLOR_NAMES).map(([hex, name]) => [name, hex])
);
const { orange, blue, yellow, green } = HEX;

// The first three scenes teach one rule each on a deliberately bare board: no
// targets, one robot crossing row 3 (clear of the centre void). Same robot,
// same row, same opening move, three different endings — the difference
// between the scenes is the only thing on screen, which is the whole lesson.
const LANE_Y = 3;
const START = { x: 1, y: LANE_Y };

// Bare of *targets*, not of walls: the board's own edges and the sealed block
// in the middle come from `buildWallMap` like everywhere else, so a scene can
// never show a robot sliding through something the board has drawn. Extra
// notches are passed in the same shape `assembleBoard` uses.
const bare = (robots, extraWalls = []) => ({
  walls: buildWallMap([], extraWalls),
  missions: [],
  activeMission: null,
  robots,
  arrangement: null,
});

// The last four scenes use a real assembled board, so what a visitor is left
// looking at is the game itself rather than a diagram of it.
const ARRANGEMENT = ['A', 'B', 'C', 'D'];
const { walls: boardWalls, missions: boardMissions } =
  assembleBoard(quadrantsData.faces ?? quadrantsData, ARRANGEMENT);

const MISSION = boardMissions.find(m => m.name === 'green-triangle');

// Verified against the solver: this is the *optimal* four, not merely a
// solution that works. Yellow spends two moves making itself into a wall at
// the left edge, green stops against it, then runs home — which is scene 3's
// lesson doing real work, and why the tutorial is worth watching to the end.
const MISSION_ROBOTS = {
  [orange]: { x: 10, y: 2 },
  [blue]: { x: 13, y: 5 },
  [yellow]: { x: 1, y: 11 },
  [green]: { x: 2, y: 12 },
};

const MISSION_MOVES = [
  { color: yellow, direction: 'down' },
  { color: yellow, direction: 'left' },
  { color: green, direction: 'left' },
  { color: green, direction: 'up' },
];

const onTheBoard = {
  walls: boardWalls,
  missions: boardMissions,
  activeMission: MISSION,
  robots: MISSION_ROBOTS,
  moves: MISSION_MOVES,
  arrangement: ARRANGEMENT,
};

export const SCENES = [
  {
    id: 'sliding',
    titleKey: 'tutorial.sliding.title',
    bodyKey: 'tutorial.sliding.body',
    ...bare({ [orange]: { ...START } }),
    moves: [{ color: orange, direction: 'right' }],
  },
  {
    id: 'walls',
    titleKey: 'tutorial.walls.title',
    bodyKey: 'tutorial.walls.body',
    ...bare({ [orange]: { ...START } }, [{ x: 7, y: LANE_Y, side: 'right' }]),
    // Two endings in one scene, because the middle of the board is the one
    // wall nothing on the tile marks — it has to be shown being hit.
    moves: [
      { color: orange, direction: 'right' },
      { color: orange, direction: 'down' },
    ],
  },
  {
    id: 'blockers',
    titleKey: 'tutorial.blockers.title',
    bodyKey: 'tutorial.blockers.body',
    ...bare({
      [orange]: { ...START },
      [blue]: { x: 12, y: LANE_Y },
    }),
    moves: [{ color: orange, direction: 'right' }],
  },
  {
    id: 'mission',
    titleKey: 'tutorial.mission.title',
    bodyKey: 'tutorial.mission.body',
    ...onTheBoard,
  },
  {
    id: 'bidding',
    titleKey: 'tutorial.bidding.title',
    bodyKey: 'tutorial.bidding.body',
    ...onTheBoard,
    roster: [
      { username: 'Ada', count: 6 },
      { username: 'Bo', count: 5 },
      { username: 'You', count: 4, you: true },
    ],
  },
  {
    id: 'skipping',
    titleKey: 'tutorial.skipping.title',
    // Deliberately says the wait ends, not the round: agreeing to skip only
    // clears the clock. With bids standing it goes straight to the demo; it is
    // a round nobody bid on that gets dropped, which is the same thing the
    // timer running out does. Saying otherwise would teach a rule the server
    // does not have (`handleReadyToSkip` in server/src/sockets/game/Game.js).
    bodyKey: 'tutorial.skipping.body',
    ...onTheBoard,
    // Nothing plays. A mission nobody bid on is a board nobody touched, and
    // that is exactly what this scene is showing.
    moves: [],
    roster: [
      { username: 'Ada', skipped: true },
      { username: 'Bo', skipped: true },
      { username: 'You', skipped: true, you: true },
    ],
  },
  {
    id: 'room',
    titleKey: 'tutorial.room.title',
    // The last thing a visitor needs is not a rule — it is the way to get the
    // other players in. Every label here is the one the app actually shows:
    // `Create a game` / `Browse rooms` / the room-code field on the menu, the
    // password choice on the create form, and `Share room` in the sidebar.
    bodyKey: 'tutorial.room.body',
    ...onTheBoard,
    // Nothing plays. A room is not a move, and the board behind this is the
    // game that is waiting for the people being invited to it.
    moves: [],
    room: {
      code: 'amber-fox',
      locked: true,
    },
  },
];

export const sceneById = (id) => SCENES.find(s => s.id === id);

export default SCENES;
